import { ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useState } from 'react';
import { useMatch } from 'react-router';
import { UserContextV2 } from '~/app';
import {
  FLOATING_BUTTON_BOTTOM,
  HIDE_WHILE_MODAL_OPEN,
} from '~/components/buttons/floatingButtons';
import { SupportRequestDialog } from '~/components/headers/HeaderHelpMenu/SupportRequestDialog';
import { useDockedSidebar } from '~/components/Layout/sidebarState';
import type { SiteBrand } from '~/config/branding';
import { useSiteMode } from '~/config/siteMode';
import { paths } from '~/routing/routes';

const TAGS = CT.layout.feedback;

/**
 * [MCV-1866] The floating "Feedback" pill on every signed-in page. It opens the same support form as
 * the header's ? menu (MCV-1780), with the Type preselected to "Report an issue", so support gets
 * the report with the sender's account details (the API derives those from the signed-in user;
 * nothing identifying is sent here).
 *
 * - Mounted by AuthenticatedRoutes, so it never renders for a signed-out visitor. It also shows on
 *   the registration pages: someone stuck in signup is exactly who needs to write in.
 * - Hidden while an employer is impersonating or delegating to a provider: the request would go out
 *   under the employer's own account while the screen shows the provider's vault. This is the same
 *   test the headers use to drop "Contact support" (NavHeader `isDelegating`, MarketHeader
 *   `isImpersonating`).
 * - Hidden on the logout page, where the session is being torn down.
 * - The brand comes from the site mode, not `useBrandConfig`: the per-route BrandProvider sits below
 *   this mount point, so there `useBrandConfig` only has its non-reactive detectBrand() fallback,
 *   while `useSiteMode` follows the router location.
 * - Bottom-left, because the Chat Support bubble holds bottom-right. Both share
 *   FLOATING_BUTTON_BOTTOM, which keeps them just above the always-visible signed-in footer, and
 *   both hide while a modal dialog is open (HIDE_WHILE_MODAL_OPEN), this one's own included.
 * - From 1360px a page inside `Layout` has its sidebar docked at the left, and its Download my data /
 *   Sign out items sit in its bottom-left corner, so the pill moves past it: the sidebar's width plus
 *   the 12px grid gap plus 4px of clearance, i.e. 288 + 12 + 4 = 304px (19rem) when expanded and
 *   56 + 12 + 4 = 72px (4.5rem) when collapsed to the icon rail [MCV-1919]. A page with no `Layout`
 *   (registration) has no sidebar, so there is no offset.
 */
export const FeedbackButton = () => {
  const [open, setOpen] = useState(false);
  const siteMode = useSiteMode();
  const onLogout = useMatch(paths.logout) !== null;
  const sidebar = useDockedSidebar();
  const userRef = UserContextV2.useActorRef();
  const isImpersonating = useSelector(userRef, (state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })
  );

  if (isImpersonating || onLogout) return null;

  const brand: SiteBrand = siteMode === 'medcredvault' ? 'mcv' : 'hlx';
  // Full class names, not interpolated pieces: Tailwind only emits what it can read.
  const sidebarOffset = !sidebar.mounted
    ? ''
    : sidebar.open
      ? 'min-[1360px]:left-[19rem]'
      : 'min-[1360px]:left-[4.5rem]';

  return (
    <>
      <button
        type="button"
        aria-label="Send feedback"
        data-cy={TAGS.trigger}
        onClick={() => setOpen(true)}
        className={`fixed ${FLOATING_BUTTON_BOTTOM} ${HIDE_WHILE_MODAL_OPEN} left-4 ${sidebarOffset} z-30 inline-flex items-center gap-1.5 bg-primary-500 text-white rounded-pill px-3 py-2 text-sm shadow-lg hover:bg-primary-600 cursor-pointer`}
      >
        <ChatBubbleBottomCenterTextIcon aria-hidden="true" className="size-4" />
        Feedback
      </button>
      <SupportRequestDialog
        open={open}
        brand={brand}
        onClose={() => setOpen(false)}
        title="Send feedback"
        intro="Report an issue or ask a question. We will reply to the email address on your account."
        defaultType="ISSUE"
      />
    </>
  );
};
